import { Bet } from "interfaces/bet";
import { Entry } from "interfaces/entry";
import { Pool } from "interfaces/pool";
import { createRef, useEffect, useRef, useState } from "react";
import cardFlip from "./card-flip.module.scss";
import BlockyHeavyText from "components/custom-texts/blocky-heavy-text";
import BlockyBoldText from "components/custom-texts/blocky-bold-text";
import { NIL_GROUP_ID, secondaryColor } from "utils/constants";
import BlockyMediumText from "components/custom-texts/blocky-medium-text";
import NumberFormat from "react-number-format";
import { useAppSelector } from "state/hooks";
import {
  getCardColor,
  getGroupCurrencySymbol,
  getGroupState,
  getTextColor,
} from "state/slices/group-slice";
import {
  formatAsCurrency,
  formattedCurrencyToNumber,
} from "utils/formatter-utils/currency-formatter";
import ChangeHighlight from "react-change-highlight";
import { calculateOdds } from "utils/oddsCalculator";
import { Button, Spinner } from "react-bootstrap";
import confirmBet from "components/pool-details/confirm-bet";
import { getIntegratedAppState } from "state/slices/integrated-app-slice";
import { X } from "react-bootstrap-icons";
import {
  resolveMinBetAmountForContestEntry,
  resolveMaxBetAmountForContestEntry,
} from "components/pool-details/entries/bet-form-helpers";
import styles from "assets/shared-stylesheets/sparket-list-item.module.css";
import highlightStyles from "components/pool-details/bet-modal/bet-modal-content/styles.module.css";
import popStyles from "./pop.module.css";
import { getBetsState } from "state/slices/bets-slice";
import useWallet from "hooks/useWallet";
import { Contest, isBasicContest, picksCostForContestPick, remainingPicksForEntry } from "interfaces/leaderboard/contest";
import { getContestEntryById } from "state/slices/contests-slice";
import { betTypeExacta, betTypeTrifecta, betTypeWin } from "interfaces/bet-type-name";

interface Props {
  entry?: Entry | null;
  pool: Pool;
  onSuccessfulBetPlaced: (bet: Bet) => void;
  contestEntryId?: string;
  contest?: Contest;
  cancel: () => void;
}

const HomepageBetForm = ({
  entry,
  pool,
  onSuccessfulBetPlaced,
  contestEntryId,
  contest,
  cancel,
}: Props) => {
  if (!entry) {
    entry = {} as Entry;
  }
  const { formattedBalance, balance } = useWallet({
    contestEntryId,
  });
  const [betAmount, setBetAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const group = useAppSelector(getGroupState);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const inputRef = useRef(null);

  const isIntegratedApp = useAppSelector(getIntegratedAppState).isIntegratedApp;
  const currencySymbol = useAppSelector(getGroupCurrencySymbol);
  const { bets } = useAppSelector(getBetsState);
  const contestantIds = (entry?.contestant_id || "")
    .split(";")
    .filter((id) => id.length > 0);
  const selectedContestants = contestantIds.map((id) => {
    const contestant = pool.contestants.find((c) => c.id === id);
    return contestant;
  });

  const numberOfSelections = selectedContestants.length;

  const isBasic = isBasicContest(contest);
  const basicPoolType =
    numberOfSelections === 3
      ? betTypeTrifecta
      : numberOfSelections === 2
      ? betTypeExacta
      : betTypeWin;
  const basicPickCost = isBasic ? picksCostForContestPick(contest, basicPoolType) : 0;
  // Picks remaining is derived from the entry, not wallet balance (which
  // inflates when pool winnings are credited back into the entry).
  const contestEntry = useAppSelector((state) => getContestEntryById(state, contestEntryId));
  const remainingPicks = remainingPicksForEntry(contestEntry);
  const basicCanAfford = isBasic ? remainingPicks >= basicPickCost : true;

  const handleSubmit = () => {
    // Validate contest entry requirement for shared pools
    if (pool.group_id === NIL_GROUP_ID && !contestEntryId) {
      setErrorMessage("You must join the contest before placing a bet");
      return;
    }

    confirmBet({
      isIntegratedApp,
      betAmount: isBasic ? String(basicPickCost) : betAmount,
      poolEntryId: entry.id || entry.pool_entry_id,
      pool_id: pool.id,
      setLoading,
      groupId: group.id,
      setErrorMessage,
      onSuccessfulBetPlaced: (bet: Bet) => {
        onSuccessfulBetPlaced(bet);
        cancel();
      },
      currency: group.currency_code,
      contest_entry_id: contestEntryId,
    });
  };

  const effectiveAmountForCalc = isBasic
    ? basicPickCost
    : formattedCurrencyToNumber(betAmount) || 0;

  const poolTotalKey = ["total_win", "total_exacta", "total_trifecta"][
    numberOfSelections - 1
  ];
  const poolTotal = (pool as any)[poolTotalKey];

  const odds = calculateOdds({
    poolTotal,
    entryTotal: entry.bet_total || entry.bets_sum || 0,
    betAmount: effectiveAmountForCalc,
    fee: pool.fee,
  });

  const projectedPayout = effectiveAmountForCalc * (odds || 0);

  const formattedProjectedPayout = formatAsCurrency(projectedPayout);

  const contestMinBetAmount = resolveMinBetAmountForContestEntry(
    bets || [],
    contestEntryId || "",
    numberOfSelections
  );

  const contestMaxBetAmount = resolveMaxBetAmountForContestEntry(
    bets || [],
    contestEntryId || "",
    numberOfSelections
  );

  useEffect(() => {
    if (inputRef && inputRef.current) {
      (inputRef.current as any).focus({ preventScroll: true });
    }

    return () => {
      if (inputRef) {
        inputRef.current = null;
      }
    };
  }, [inputRef, entry]);

  useEffect(() => {
    if (isBasic) {
      setErrorMessage(undefined);
      return;
    }
    const hasBalance = balance !== undefined;
    const hasBets = bets !== undefined;
    const hasContestEntryId = contestEntryId !== undefined;
    const remainingBalance =
      (balance || 0) - formattedCurrencyToNumber(betAmount);

    const showMinBetAmountText =
      hasBets &&
      hasContestEntryId &&
      formattedCurrencyToNumber(betAmount) < contestMinBetAmount &&
      contestMinBetAmount > 0;

    const showMaxBetAmountText =
      hasBets &&
      hasContestEntryId &&
      formattedCurrencyToNumber(betAmount) > contestMaxBetAmount &&
      contestMaxBetAmount < Number.MAX_SAFE_INTEGER;

    if (showMinBetAmountText) {
      setErrorMessage(
        `Your picks must be at least ${formatAsCurrency(
          contestMinBetAmount
        )}`
      );
    } else if (showMaxBetAmountText) {
      setErrorMessage(
        `Your picks can be at most ${formatAsCurrency(
          contestMaxBetAmount
        )}`
      );
    } else {
      setErrorMessage(undefined);
    }
  }, [
    betAmount,
    balance,
    bets,
    contestEntryId,
    contestMinBetAmount,
    contestMaxBetAmount,
  ]);

  const textColor = useAppSelector(getTextColor);
  const cardColor = useAppSelector(getCardColor);

  const valueStyle = {
    color: "var(--text-color)",
    fontWeight: 800 as const,
  };
  const grayLabelClass = styles.poolCloseDatePrefix;

  return (
    <div
      className={styles.itemContainer + " " + cardFlip.cardFlipBack}
      style={{
        minHeight: 90,
        marginLeft: 0,
        marginRight: 0,
        height: 250,
        fontSize: 12,
        backgroundColor: cardColor,
        color: textColor,
      }}
    >
      <div
        style={{
          width: "100%",
          padding: 5,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div>
            <BlockyHeavyText style={{ fontSize: 10 }}>
              {pool.event_name}
            </BlockyHeavyText>
            <BlockyHeavyText className={styles.poolNameText}>
              {pool.name}
            </BlockyHeavyText>
          </div>
          <div style={{ marginTop: -5 }} onClick={() => cancel()}>
            <X size={30} />
          </div>
        </div>

        <div>
          <BlockyHeavyText
            className={grayLabelClass}
            style={{ marginRight: 0 }}
          >
            Win Condition:{" "}
          </BlockyHeavyText>
          <BlockyMediumText
            style={{ fontSize: 12, display: "inline", lineHeight: 0.5 }}
          >
            {pool.win_condition}
          </BlockyMediumText>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <BlockyHeavyText
              className={popStyles.pop}
              style={{
                fontSize: 16,
                ...valueStyle,
                wordBreak: "break-word",
              }}
            >
              {entry.contestant_name}
            </BlockyHeavyText>
          </div>
          {isBasic ? (
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 8,
                paddingRight: 12,
              }}
            >
              <BlockyHeavyText className={grayLabelClass}>
                Picks Left:
              </BlockyHeavyText>
              <BlockyBoldText
                className={popStyles.pop}
                style={{
                  fontSize: 16,
                  ...valueStyle,
                  lineHeight: 1,
                }}
              >
                {contestEntry ? remainingPicks : "--"}
              </BlockyBoldText>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 110,
                paddingRight: 12,
              }}
            >
              <BlockyHeavyText
                className={grayLabelClass}
                style={{ textAlign: "center", marginRight: 0 }}
              >
                Balance
              </BlockyHeavyText>
              <BlockyBoldText
                className={popStyles.pop}
                style={{
                  fontSize: 16,
                  ...valueStyle,
                  lineHeight: 1.1,
                  textAlign: "center",
                }}
              >
                {formattedBalance}
              </BlockyBoldText>
            </div>
          )}
        </div>

        <div>
          {errorMessage && (
            <div style={{ marginTop: 5, color: "red", fontSize: 12 }}>
              {errorMessage}
            </div>
          )}
          {isBasic ? (
            <div style={{ padding: 3 }}>
              <Button
                style={{
                  width: "100%",
                  height: 41,
                  backgroundColor: secondaryColor,
                  color: "black",
                }}
                disabled={loading || !basicCanAfford}
                onClick={handleSubmit}
              >
                {loading && (
                  <Spinner
                    animation="border"
                    size="sm"
                    style={{ marginRight: 10 }}
                  />
                )}
                Confirm
              </Button>
              <div
                style={{
                  marginTop: 7,
                  display: "flex",
                  alignItems: "baseline",
                }}
              >
                <BlockyHeavyText className={grayLabelClass}>
                  Exp. Return:
                </BlockyHeavyText>
                <ChangeHighlight
                  hideAfter={1000}
                  highlightClassName={highlightStyles.highlight}
                >
                  <span
                    ref={createRef()}
                    className={popStyles.pop}
                    style={{ ...valueStyle, fontSize: 16, lineHeight: 1 }}
                  >
                    {formattedProjectedPayout}
                  </span>
                </ChangeHighlight>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", padding: 3 }}>
              <div style={{ flex: 0.5 }}>
                <NumberFormat
                  id="input-BetModalContent-betAmountInput"
                  getInputRef={inputRef}
                  onValueChange={(value) => {
                    if (!value.floatValue) {
                      setBetAmount("$0.00");
                    }

                    if (
                      balance !== undefined &&
                      formattedCurrencyToNumber(value.formattedValue) <=
                        balance!
                    ) {
                      setBetAmount(value.formattedValue);
                    }
                  }}
                  prefix={currencySymbol}
                  decimalScale={2}
                  thousandSeparator
                  inputMode="decimal"
                  fixedDecimalScale
                  allowNegative={false}
                  disabled={loading}
                  isAllowed={(value) => {
                    if (balance === undefined) {
                      return false;
                    }
                    if (value && value.floatValue) {
                      return value && value?.floatValue <= balance!;
                    }
                    return true;
                  }}
                  placeholder="Enter Amount"
                  style={{
                    borderRadius: 5,
                    height: 40,
                    color: "black",
                    width: "95%",
                    fontSize: 16,
                  }}
                />
                <div
                  style={{
                    marginTop: 7,
                    display: "flex",
                    alignItems: "baseline",
                  }}
                >
                  <BlockyHeavyText className={grayLabelClass}>
                    Odds:
                  </BlockyHeavyText>
                  <ChangeHighlight
                    hideAfter={1000}
                    highlightClassName={highlightStyles.highlight}
                  >
                    <div
                      ref={createRef()}
                      className={popStyles.pop}
                      style={{ ...valueStyle, fontSize: 16, lineHeight: 1 }}
                    >{`${odds.toFixed(2)}X`}</div>
                  </ChangeHighlight>
                </div>
              </div>
              <div style={{ width: "100%", flex: 0.5 }}>
                <Button
                  style={{
                    width: "100%",
                    height: 41,
                    backgroundColor: secondaryColor,
                    color: "black",
                  }}
                  disabled={
                    loading ||
                    !betAmount ||
                    betAmount === "$0.00" ||
                    (bets &&
                      contestEntryId !== undefined &&
                      formattedCurrencyToNumber(betAmount) <
                        contestMinBetAmount &&
                      contestMinBetAmount > 0) ||
                    (bets &&
                      contestEntryId !== undefined &&
                      formattedCurrencyToNumber(betAmount) >
                        contestMaxBetAmount &&
                      contestMaxBetAmount < Number.MAX_SAFE_INTEGER)
                  }
                  onClick={handleSubmit}
                >
                  {loading && (
                    <Spinner
                      animation="border"
                      size="sm"
                      style={{ marginRight: 10 }}
                    />
                  )}
                  Confirm
                </Button>
                <div
                  style={{
                    marginTop: 7,
                    display: "flex",
                    alignItems: "baseline",
                  }}
                >
                  <BlockyHeavyText className={grayLabelClass}>
                    Exp. Return:
                  </BlockyHeavyText>
                  <ChangeHighlight
                    hideAfter={1000}
                    highlightClassName={highlightStyles.highlight}
                  >
                    <span
                      ref={createRef()}
                      className={popStyles.pop}
                      style={{ ...valueStyle, fontSize: 16, lineHeight: 1 }}
                    >
                      {formattedProjectedPayout}
                    </span>
                  </ChangeHighlight>
                </div>
              </div>
            </div>
          )}
        </div>
        <BlockyMediumText style={{ fontSize: 9, color: "gray" }}>
          Expected returns are subject to change based on subsequent activity in
          the pool.
        </BlockyMediumText>
      </div>
    </div>
  );
};

export default HomepageBetForm;
